import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, MessageCircle, Phone, ArrowRight, Gift, Clock, Star } from 'lucide-react';
import { useErrorPageData } from '@/hooks/useErrorPageData';
import { useActiveBanners } from '@/hooks/usePromotionalBanners';
import { ORDER_SITE_URL } from '@/lib/externalLinks';

interface CustomErrorPageProps {
  title?: string;
  message?: string;
  showRetry?: boolean;
  onRetry?: () => void;
}

const CustomErrorPage: React.FC<CustomErrorPageProps> = ({
  title = "Oops! Something Sweet Went Wrong",
  message = "Don't let a technical hiccup ruin your celebration plans!",
  showRetry = true,
  onRetry
}) => {
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  const { packages, discountCode, loading, error } = useErrorPageData();
  const { data: activeBanners = [] } = useActiveBanners('error');

  // Hardcoded contact info and trust signals (not configurable via admin)
  const contactInfo = {
    whatsapp: '+233201100533',
    phone: '+233201100533'
  };

  const trustSignals = {
    totalCustomers: 2000,
    fiveStarReviews: 500,
    deliveryGuarantee: '30-Min',
    freshness: '100%'
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const quickOrderOptions = [
    {
      title: "WhatsApp Order",
      subtitle: "Order instantly via chat",
      icon: MessageCircle,
      action: ORDER_SITE_URL,
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50"
    },
    {
      title: "Call Us Direct",
      subtitle: "Speak to our celebration team",
      icon: Phone,
      action: `tel:${contactInfo.phone}`,
      color: "from-blue-500 to-indigo-500",
      bgColor: "bg-blue-50"
    }
  ];

  // Use only packages from database - no fallbacks
  const emergencyPackages = packages;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 w-full">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 w-full relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/celebration/premium-celebration-doughnuts-hero-background-lg.webp"
            alt="Premium celebration doughnuts background"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = "/images/celebration/premium-celebration-doughnuts-hero-background-lg.jpeg"; }}
          />
        </div>

        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden shadow-xl">
              <img
                src="/images/celebration/glazed-chocolate-doughnut-rainbow-sprinkles-macro-md.webp"
                alt="Don't worry, the doughnuts are still delicious"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = "/images/celebration/glazed-chocolate-doughnut-rainbow-sprinkles-macro-md.jpeg"; }}
              />
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {title}
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
              {message} Our doughnuts are still fresh, and we're here to help you celebrate!
            </p>

            {/* Limited Time Offer Alert */}
            {discountCode && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-8">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-red-500 mr-2" />
                  <div>
                    <p className="text-red-800 font-semibold">Special Error Page Discount!</p>
                    <p className="text-red-600 text-sm">
                      Use code {discountCode.code} for {discountCode.value}% off - expires in {formatTime(timeLeft)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {showRetry && (
                <Button
                  onClick={handleRetry}
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full px-6 py-3"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              )}

              <Button
                asChild
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50 rounded-full px-6 py-3"
              >
                <a href="/">
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Go to Homepage
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Banners */}
      {activeBanners.length > 0 && (
        <section className="w-full">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
            {activeBanners.map((banner) => (
              <div
                key={banner.id}
                className={`${banner.background_color} ${banner.text_color} rounded-xl p-4 shadow-md`}
              >
                <div className="flex items-center gap-3">
                  {banner.icon && <span className="text-2xl">{banner.icon}</span>}
                  <div className="flex-1">
                    <p className="font-semibold">{banner.title}</p>
                    <p className="text-sm opacity-90">{banner.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Alternative Ordering Methods */}
      <section className="py-12 bg-white w-full">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-800">Don't Give Up!</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Order Your Celebration Right Now
            </h2>
            <p className="text-lg text-gray-600">
              Technical issues happen, but your celebration doesn't have to wait
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {quickOrderOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <div
                  key={index}
                  className={`${option.bgColor} rounded-2xl p-8 text-center hover:shadow-lg transition-shadow cursor-pointer`}
                  onClick={() => window.open(option.action, '_blank')}
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${option.color} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{option.title}</h3>
                  <p className="text-gray-600 mb-4">{option.subtitle}</p>
                  <Button className={`bg-gradient-to-r ${option.color} text-white rounded-full`} asChild>
                    <a href={ORDER_SITE_URL}>
                      Order Now <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emergency Packages - Only show if packages exist */}
      {emergencyPackages.length > 0 && (
        <section className="py-12 bg-gray-50 w-full">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-800">Quick Order Options</Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Ready-to-Go Celebration Packages
              </h2>
              <p className="text-lg text-gray-600">
                Order instantly via WhatsApp or phone
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {emergencyPackages.map((pkg, index) => (
                <div key={pkg.id || index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="relative mb-6">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-48 object-cover rounded-xl"
                      onError={(e) => { e.currentTarget.src = pkg.imageFallback; }}
                    />
                    {pkg.urgent && (
                      <Badge className="absolute top-2 right-2 bg-red-500 text-white">
                        30 Min Ready
                      </Badge>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-4">{pkg.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-amber-600">
                        {typeof pkg.price === 'string' ? pkg.price : `₵${pkg.price}`}
                      </span>
                      <span className="text-gray-500 line-through text-lg">
                        {typeof pkg.originalPrice === 'string' ? pkg.originalPrice : `₵${pkg.originalPrice}`}
                      </span>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Save ₵{typeof pkg.originalPrice === 'string' ? '10' : Math.round(pkg.originalPrice - pkg.price)}
                      </Badge>
                    </div>
                    <div className="flex items-center">
                      {[1,2,3,4,5].map((star) => (
                        <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full"
                    asChild
                  >
                    <a href={ORDER_SITE_URL}>
                      <Gift className="mr-2 h-4 w-4" />
                      {discountCode ? `Order with ${discountCode.code} Code` : 'Order Now'}
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust Signals */}
      <section className="py-12 bg-gray-900 text-white w-full">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-4">Why Order Despite the Technical Hiccup?</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400 mb-2">{trustSignals.totalCustomers.toLocaleString()}+</div>
              <div className="text-gray-400">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <div className="text-gray-400">{trustSignals.fiveStarReviews.toLocaleString()}+ Five-Star Reviews</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400 mb-2">{trustSignals.deliveryGuarantee}</div>
              <div className="text-gray-400">Delivery Guarantee</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400 mb-2">{trustSignals.freshness}</div>
              <div className="text-gray-400">Fresh Daily</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomErrorPage;
